/**
 * SalaryRangeBand — slim horizontal bar showing the LEO P25–P75
 * salary range with a tick at the median. Rendered under each
 * course row on the Highest-Earning page so families see the
 * spread at a glance — "£45k median but half earn £30-60k".
 *
 * Props:
 *   lower_gbp / upper_gbp / median_gbp : the three LEO numbers.
 *   scale_min_gbp / scale_max_gbp     : the shared visible range
 *     across every row on the same page — passing the same scale
 *     to every SalaryRangeBand keeps rows visually comparable
 *     (a wide P25-P75 in one row is genuinely wider than a
 *     narrow one in another).
 *   currency_symbol : £ or $ (defaults to £ for legacy LEO usage).
 */
export default function SalaryRangeBand({
  lower_gbp,
  upper_gbp,
  median_gbp,
  scale_min_gbp,
  scale_max_gbp,
  currency_symbol = "£",
  testid,
}) {
  if (!median_gbp || !lower_gbp || !upper_gbp) return null;
  if (upper_gbp <= lower_gbp) return null;
  if (!scale_min_gbp || !scale_max_gbp || scale_max_gbp <= scale_min_gbp) return null;

  const range = scale_max_gbp - scale_min_gbp;
  const clip = (v) => Math.min(1, Math.max(0, (v - scale_min_gbp) / range));
  const leftPct = clip(lower_gbp) * 100;
  const rightPct = clip(upper_gbp) * 100;
  const medianPct = clip(median_gbp) * 100;

  const kfmt = (v) => `${currency_symbol}${Math.round(v / 1000)}k`;

  return (
    <div
      className="mt-1 select-none"
      data-testid={testid}
      title={`IQR ${kfmt(lower_gbp)} – ${kfmt(upper_gbp)} · median ${kfmt(median_gbp)}`}
    >
      <div className="relative h-1.5 rounded-full bg-stone-100 overflow-visible">
        {/* P25 → P75 band */}
        <div
          className="absolute inset-y-0 rounded-full bg-emerald-200"
          style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
          aria-hidden
        />
        {/* Median tick — narrower than the band so it visually reads
            as a marker on the bar rather than another segment. */}
        <div
          className="absolute w-0.5 h-3 bg-emerald-700 -top-0.5 rounded-sm"
          style={{ left: `calc(${medianPct}% - 1px)` }}
          aria-hidden
        />
      </div>
      <div className="mt-1 flex items-center justify-between font-mono text-[10px] text-stone-500">
        <span>{kfmt(lower_gbp)}</span>
        <span className="text-stone-800 font-semibold">
          median <span className="text-emerald-700">{kfmt(median_gbp)}</span>
        </span>
        <span>{kfmt(upper_gbp)}</span>
      </div>
    </div>
  );
}
